import React, { useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { assetUrl, fetchJSON } from '../../utils/api';
import { cartTotal, saveMerchCart } from '../../utils/merchCart';

const initialCheckout = {
  customerFirstName: '',
  customerLastName: '',
  customerEmail: '',
  customerPhone: '',
  deliveryAddress: '',
  deliveryPostcode: '',
  deliveryCity: '',
  deliveryCountry: '',
  paymentMethod: 'Vipps',
  message: '',
};

function formatNok(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return '0 NOK';
  return `${n.toLocaleString('nb-NO')} NOK`;
}

function ensureQty(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 1;
  return Math.round(n);
}

function deriveRequestedSizeFromVariants(selectedVariants) {
  const rows = Array.isArray(selectedVariants) ? selectedVariants : [];
  const sizeRow = rows.find((entry) => /size|stoerrelse|størrelse/i.test(String(entry?.title || '')));
  return sizeRow ? String(sizeRow.value || '').trim().toUpperCase() : '';
}

function isDomesticCountry(value) {
  const country = String(value || '').trim().toUpperCase();
  if (!country) return true;
  return country === 'NO' || country === 'NOR' || country === 'NORWAY' || country === 'NORGE';
}

const MerchandiseCartModal = ({
  open,
  cartItems,
  setCartItems,
  shippingCostNok = 75,
  shippingCostInternationalNok = 150,
  onClose,
  onOrderCompleted,
}) => {
  const [checkout, setCheckout] = useState(initialCheckout);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMessage('');
    setSubmitting(false);
  }, [open]);

  const subtotal = useMemo(() => cartTotal(cartItems), [cartItems]);
  const normalizedShippingCostNok = useMemo(() => {
    const parsed = Number(shippingCostNok);
    if (!Number.isFinite(parsed) || parsed < 0) return 75;
    return Math.round(parsed);
  }, [shippingCostNok]);
  const normalizedShippingCostInternationalNok = useMemo(() => {
    const parsed = Number(shippingCostInternationalNok);
    if (!Number.isFinite(parsed) || parsed < 0) return 150;
    return Math.round(parsed);
  }, [shippingCostInternationalNok]);
  const checkoutCountry = String(checkout.deliveryCountry || '').trim();
  const shippingFee = useMemo(
    () => {
      if (!(Array.isArray(cartItems) && cartItems.length > 0)) return 0;
      return isDomesticCountry(checkoutCountry) ? normalizedShippingCostNok : normalizedShippingCostInternationalNok;
    },
    [cartItems, checkoutCountry, normalizedShippingCostInternationalNok, normalizedShippingCostNok]
  );
  const total = subtotal + shippingFee;
  const mobileButtonStyle = isMobile
    ? {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1.2,
      boxSizing: 'border-box',
      textAlign: 'center',
    }
    : {};

  const updateItem = (index, patch) => {
    const next = (Array.isArray(cartItems) ? cartItems : []).map((item, i) => (i === index ? { ...item, ...patch } : item));
    setCartItems(next);
    saveMerchCart(next);
  };

  const removeItem = (index) => {
    const next = (Array.isArray(cartItems) ? cartItems : []).filter((_item, i) => i !== index);
    setCartItems(next);
    saveMerchCart(next);
  };

  const updateVariantForItem = (index, title, value) => {
    const t = String(title || '').trim();
    if (!t) return;
    const v = String(value || '').trim();

    const next = (Array.isArray(cartItems) ? cartItems : []).map((item, i) => {
      if (i !== index) return item;
      const selected = Array.isArray(item.selectedVariants)
        ? item.selectedVariants.map((row) => ({ title: String(row?.title || '').trim(), value: String(row?.value || '').trim() })).filter((row) => row.title)
        : [];
      const existingIndex = selected.findIndex((row) => row.title === t);
      if (existingIndex >= 0) {
        selected[existingIndex] = { title: t, value: v };
      } else {
        selected.push({ title: t, value: v });
      }
      const selectedVariants = selected.filter((row) => row.title && row.value);
      return {
        ...item,
        selectedVariants,
        requestedSize: deriveRequestedSizeFromVariants(selectedVariants) || item.requestedSize || '',
      };
    });

    setCartItems(next);
    saveMerchCart(next);
  };

  const submitOrder = async (e) => {
    e.preventDefault();
    const customerFirstName = String(checkout.customerFirstName || '').trim();
    const customerLastName = String(checkout.customerLastName || '').trim();
    const customerEmail = String(checkout.customerEmail || '').trim();
    const deliveryAddress = String(checkout.deliveryAddress || '').trim();
    const deliveryPostcode = String(checkout.deliveryPostcode || '').trim();
    const deliveryCity = String(checkout.deliveryCity || '').trim();
    const deliveryCountry = String(checkout.deliveryCountry || '').trim();
    const paymentMethod = String(checkout.paymentMethod || '').trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail);

    if (!cartItems.length) {
      setMessage('DØDSEGO CART er tom.');
      return;
    }
    if (!customerFirstName || !customerLastName || !emailOk) {
      setMessage('Skriv inn fornavn, etternavn og gyldig e-post.');
      return;
    }
    if (!deliveryAddress || !deliveryPostcode || !deliveryCity || !deliveryCountry) {
      setMessage('Leveringsadresse, postkode, by og land er paakrevd.');
      return;
    }
    if (!paymentMethod) {
      setMessage('Velg betalingsmetode.');
      return;
    }

    setSubmitting(true);
    setMessage('');
    try {
      await fetchJSON('/api/merchandise-orders/public', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerFirstName,
          customerLastName,
          customerEmail,
          customerPhone: String(checkout.customerPhone || '').trim(),
          deliveryAddress,
          deliveryPostcode,
          deliveryCity,
          deliveryCountry,
          paymentMethod,
          message: String(checkout.message || '').trim(),
          items: cartItems.map((item) => ({
            productId: item.productId,
            itemTitle: item.itemTitle,
            itemImageSrc: item.itemImageSrc,
            saleType: item.saleType || 'Other',
            salePriceNok: Number(item.salePriceNok || 0) || 0,
            quantity: ensureQty(item.quantity),
            requestedSize: String(item.requestedSize || '').trim().toUpperCase(),
            selectedVariants: Array.isArray(item.selectedVariants)
              ? item.selectedVariants.map((entry) => ({
                title: String(entry?.title || '').trim(),
                value: String(entry?.value || '').trim(),
              })).filter((entry) => entry.title && entry.value)
              : [],
          })),
        }),
      });

      setCartItems([]);
      saveMerchCart([]);
      setCheckout(initialCheckout);
      setSuccessOpen(true);
    } catch (error) {
      const text = String(error?.message || '');
      if (text.includes('customer_email_delivery_failed')) {
        setMessage('E-postadressen ser ut til aa vaere feil eller kan ikke motta e-post. Kontroller adressen og proev igjen.');
      } else {
        setMessage('Kunne ikke sende ordren. Proev igjen.');
      }
    } finally {
      setSubmitting(false);
    }

  };

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'max(10px, env(safe-area-inset-top)) max(10px, env(safe-area-inset-right)) max(10px, env(safe-area-inset-bottom)) max(10px, env(safe-area-inset-left))' }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ width: '100%', maxWidth: 'min(1150px, calc(100vw - 20px))', maxHeight: 'calc(100vh - 20px)', overflowY: 'auto', overflowX: 'hidden', borderRadius: 14, border: '1px solid rgba(255,255,255,0.24)', background: '#0d0d0d', color: '#fff', padding: 'clamp(12px, 2.4vw, 30px)', display: 'grid', gap: 14, boxSizing: 'border-box' }}
        >
          <h3 style={{ margin: 0 }}>DØDSEGO CART</h3>

          <div style={{ display: 'grid', gap: 10 }}>
            {(Array.isArray(cartItems) ? cartItems : []).map((item, index) => (
              <div key={`${item.productId || item.itemTitle}-${index}`} style={{ display: 'grid', gridTemplateColumns: '90px minmax(0, 1fr) auto', gap: 10, alignItems: 'stretch', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 10, padding: 10, maxWidth: '100%', boxSizing: 'border-box' }}>
                <div style={{ width: 90, height: '100%', minHeight: 90, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)', background: '#111' }}>
                  {item.itemImageSrc ? (
                    <img src={assetUrl(item.itemImageSrc)} alt={item.itemTitle} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : null}
                </div>
                <div style={{ display: 'grid', gap: 5, minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{item.itemTitle}</div>
                  <div style={{ opacity: 0.85, fontSize: 13 }}>{formatNok(item.salePriceNok)}</div>
                  {Array.isArray(item.variantGroups) && item.variantGroups.length ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      {item.variantGroups.map((group, variantIndex) => {
                        const title = String(group?.title || '').trim();
                        const options = Array.isArray(group?.options) ? group.options.map((opt) => String(opt || '').trim()).filter(Boolean) : [];
                        if (!title || !options.length) return null;
                        const selectedValue = (Array.isArray(item.selectedVariants) ? item.selectedVariants : []).find((row) => String(row?.title || '').trim() === title)?.value || '';
                        return (
                          <label key={`${title}-${variantIndex}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%', flexWrap: 'wrap' }}>
                            <span style={{ opacity: 1, color: '#f2f6ff', fontSize: 12, fontWeight: 700 }}>{title}</span>
                            <select
                              value={selectedValue}
                              onChange={(e) => updateVariantForItem(index, title, e.target.value)}
                              style={{ width: 'min(170px, 100%)', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '7px 9px' }}
                            >
                              <option value=''>Select {title.toLowerCase()}</option>
                              {options.map((opt) => (
                                <option key={`${title}-${opt}`} value={opt}>{opt}</option>
                              ))}
                            </select>
                          </label>
                        );
                      })}
                    </div>
                  ) : Array.isArray(item.selectedVariants) && item.selectedVariants.length ? (
                    <div style={{ display: 'grid', gap: 3 }}>
                      {item.selectedVariants.map((entry, variantIndex) => (
                        <div key={`${entry.title}-${entry.value}-${variantIndex}`} style={{ opacity: 0.85, fontSize: 12 }}>
                          {entry.title}: {entry.value}
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {item.saleType === 'T-shirt' && !(Array.isArray(item.variantGroups) && item.variantGroups.length) ? (
                    <input
                      value={item.requestedSize || ''}
                      onChange={(e) => updateItem(index, { requestedSize: String(e.target.value || '').toUpperCase().slice(0, 20) })}
                      placeholder='Size'
                      style={{ width: 130, minWidth: 0, boxSizing: 'border-box', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '7px 9px' }}
                    />
                  ) : null}
                </div>
                <div style={{ display: 'grid', gap: 8, justifyItems: 'end', minWidth: 0 }}>
                  <input
                    type='number'
                    min='1'
                    value={ensureQty(item.quantity)}
                    onChange={(e) => updateItem(index, { quantity: ensureQty(e.target.value) })}
                    style={{ width: 80, minWidth: 0, boxSizing: 'border-box', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '7px 9px' }}
                  />
                  <button type='button' onClick={() => removeItem(index)} style={{ ...mobileButtonStyle, borderRadius: 999, border: '1px solid rgba(255,110,110,0.6)', background: 'rgba(140,0,0,0.25)', color: '#ffd9d9', padding: isMobile ? '9px 10px' : '6px 10px', cursor: 'pointer', maxWidth: '100%' }}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
            {!cartItems.length ? <div style={{ opacity: 0.85 }}>No merchandise products in the cart yet..</div> : null}
          </div>

          <div style={{ display: 'grid', gap: 6, maxWidth: 620, width: '100%', margin: '0 auto', textAlign: 'left', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, padding: 12, background: 'rgba(255,255,255,0.03)' }}>
            {(Array.isArray(cartItems) ? cartItems : []).map((item, index) => {
              const qty = ensureQty(item.quantity);
              const lineTotal = Math.max(0, Number(item.salePriceNok || 0) || 0) * qty;
              return (
                <div key={`sum-${item.productId || item.itemTitle}-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center', fontSize: 14 }}>
                  <span>{item.itemTitle}{qty > 1 ? ` x ${qty}` : ''}</span>
                  <span>{formatNok(lineTotal)}</span>
                </div>
              );
            })}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center', fontSize: 14 }}>
              <span>Freight and administration</span>
              <span>{formatNok(shippingFee)}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center', fontWeight: 700, fontSize: 18, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.18)' }}>
              <span>Total</span>
              <span>{formatNok(total)}</span>
            </div>
          </div>

          <form onSubmit={submitOrder} autoComplete='on' style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 8 }}>
              <input name='given-name' autoComplete='given-name' value={checkout.customerFirstName} onChange={(e) => setCheckout((prev) => ({ ...prev, customerFirstName: e.target.value }))} placeholder='First name' required style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px' }} />
              <input name='family-name' autoComplete='family-name' value={checkout.customerLastName} onChange={(e) => setCheckout((prev) => ({ ...prev, customerLastName: e.target.value }))} placeholder='Last name' required style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 8 }}>
              <input name='email' autoComplete='email' type='email' value={checkout.customerEmail} onChange={(e) => setCheckout((prev) => ({ ...prev, customerEmail: e.target.value }))} placeholder='E-mail' required style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px' }} />
              <input name='tel' autoComplete='tel' value={checkout.customerPhone} onChange={(e) => setCheckout((prev) => ({ ...prev, customerPhone: e.target.value }))} placeholder='Phone (optional)' style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8 }}>
              <input name='address-line1' autoComplete='address-line1' value={checkout.deliveryAddress} onChange={(e) => setCheckout((prev) => ({ ...prev, deliveryAddress: e.target.value }))} placeholder='Address' required style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px' }} />
              <input name='postal-code' autoComplete='postal-code' value={checkout.deliveryPostcode} onChange={(e) => setCheckout((prev) => ({ ...prev, deliveryPostcode: e.target.value }))} placeholder='Postal code' required style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px' }} />
              <input name='address-level2' autoComplete='address-level2' value={checkout.deliveryCity} onChange={(e) => setCheckout((prev) => ({ ...prev, deliveryCity: e.target.value }))} placeholder='City' required style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px' }} />
              <input name='country-name' autoComplete='country-name' value={checkout.deliveryCountry} onChange={(e) => setCheckout((prev) => ({ ...prev, deliveryCountry: e.target.value }))} placeholder='Country' required style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px' }} />
            </div>
            <div style={{ fontSize: 13, opacity: 0.92, marginTop: 4 }}>
              Choose payment method. We will contact you per e-mail to complete the payment of the order as well as planning the shipment.
            </div>
            <select
              value={checkout.paymentMethod}
              onChange={(e) => setCheckout((prev) => ({ ...prev, paymentMethod: e.target.value }))}
              style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px' }}
            >
              <option value='Vipps'>Vipps</option>
              <option value='MobilePay'>MobilePay</option>
              <option value='Card'>CARD</option>
              <option value='Invoice'>Invoice</option>
              <option value='PayPal'>PayPal</option>
            </select>
            <textarea value={checkout.message} onChange={(e) => setCheckout((prev) => ({ ...prev, message: e.target.value }))} rows={3} placeholder='Message (optional)' style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px' }} />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type='button' onClick={onClose} style={{ ...mobileButtonStyle, borderRadius: 8, border: '1px solid #444', background: '#111', color: '#fff', padding: isMobile ? '12px 11px' : '8px 11px', cursor: 'pointer' }}>Lukk</button>
              <button type='submit' disabled={submitting || !cartItems.length} style={{ ...mobileButtonStyle, borderRadius: 8, border: '1px solid #fff', background: '#000', color: '#fff', padding: isMobile ? '12px 11px' : '8px 11px', cursor: 'pointer' }}>{submitting ? 'Sender...' : 'SEND ORDRE'}</button>
            </div>
          </form>

          {message ? <div style={{ marginTop: 4, fontSize: 14, opacity: 0.95 }}>{message}</div> : null}
        </div>
      </div>

      {successOpen ? (
        <div
          onClick={() => {
            setSuccessOpen(false);
            onClose?.();
            onOrderCompleted?.();
          }}
          style={{ position: 'fixed', inset: 0, zIndex: 1400, background: 'rgba(0,0,0,0.78)', display: 'grid', placeItems: 'center', padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(520px, 94vw)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.28)', background: '#0b0b0b', color: '#fff', padding: 20, display: 'grid', gap: 10 }}
          >
            <h3 style={{ margin: 0 }}>Takk! Ordren er sendt.</h3>
            <p style={{ margin: '6px 0 10px', opacity: 1, color: '#fff', fontSize: 17, lineHeight: 1.55 }}>Du vil snart motta e-post med ordrebekreftelse og videre informasjon.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type='button'
                onClick={() => {
                  setSuccessOpen(false);
                  onClose?.();
                  onOrderCompleted?.();
                }}
                style={{ ...mobileButtonStyle, borderRadius: 8, border: '1px solid #fff', background: '#000', color: '#fff', padding: isMobile ? '12px 12px' : '8px 12px', cursor: 'pointer' }}
              >
                Til forsiden
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default MerchandiseCartModal;
